import { FC, useState, useCallback } from 'react';
import Navigation from './Navigation';
import NavigationControls from './NavigationControls';
import useStepper from './hooks/useStepper';
import { Step } from './types';
import FormWizardContext, { SetValueFn, ValidateStepFn } from '../contexts/form-wizard-context';
import { RequestPublicForm } from '../types';

type FormWizardProps = {
    steps: Step[];
    isShowNavigation: boolean;
    isShowNavigationControls: boolean;
    isShowDisabledNavigationControls?: boolean;
    defaultState: RequestPublicForm;
    defaultStepIndex?: number;
    onStepChange?: (currentStep: Step) => void;
    onFinalStepChange?: (data: RequestPublicForm, currentStep: Step) => void;
};

const FormWizard: FC<FormWizardProps> = ({
    steps,
    isShowNavigation,
    isShowNavigationControls,
    isShowDisabledNavigationControls,
    defaultStepIndex,
    defaultState,
    onStepChange,
    onFinalStepChange
}: FormWizardProps) => {
    const [formState, setFormState] = useState<RequestPublicForm>(defaultState);

    const setValue: SetValueFn = useCallback(
        (key, value) => setFormState(prevState => ({ ...prevState, [key]: value })),
        []
    );

    const {
        currentStep,
        currentStepIndex,
        isFirstStep,
        isLastStep,
        isValidStep,
        goToPreviousStep,
        goToNextStep,
        completeFinalStep,
        setIsValidStep
    } = useStepper({
        steps,
        defaultStepIndex,
        onStepChange,
        onFinalStepChange: (currentStep) => {
            if (!onFinalStepChange) {
                return;
            }
            
            onFinalStepChange(formState, currentStep);
        }
    });
    
    const validateStep: ValidateStepFn = (callback) => {
        setIsValidStep(callback());
    };
    
    const {
        component: Component
    } = currentStep;
    
    return (
        <FormWizardContext.Provider value={{data: formState, setValue, validateStep}}>
            <div className="p-4 sm:p-6 lg:p-8">
                {isShowNavigation && (
                    <div className="pb-4 sm:pb-6 lg:pb-8">
                        <Navigation
                            steps={steps}
                            currentStepIndex={currentStepIndex}
                        />
                    </div>
                )}
    
                <Component />

                {isShowNavigationControls && (
                    <div className="flex pt-4 sm:pt-6 lg:pt-8">
                        <NavigationControls
                            steps={steps}
                            isFirstStep={isFirstStep}
                            isLastStep={isLastStep}
                            isValidStep={isValidStep}
                            onGoToPreviousStep={goToPreviousStep}
                            onGoToNextStep={goToNextStep}
                            onCompleteFinalStep={completeFinalStep}
                            isHideOnInactive={isShowDisabledNavigationControls}
                        />
                    </div>
                )}
            </div>
        </FormWizardContext.Provider>
    );
};

export default FormWizard;